/**
 * QMP-FE-8e1 — THE FULL-SCREEN RULE EDITOR OF v83 (docs/design/mockups/
 * mockup-qmp-v83-official.html: `editorPage` l. 2061–2077, `editorFooter`
 * l. 2078–2083, `scopePicker` l. 1923–1925, `deadlineFields` l. 1962–1985,
 * `durField` l. 1733–1741, `restoreBox` l. 1953–1957, the "Unsaved changes"
 * guard l. 2283–2303; dark values of the `#cqa-qmp-flow` tokens). For the
 * three rules of step 2: ITP preparation and endorsement, hold points,
 * witness points. Steps 3 and 4 keep today's editor (8e2, 8e3).
 *
 * QMP-FE-8e2 — and the four rules of step 3 (`deadlineFields` l. 1980–1998):
 * NCRs and RFIs ("Deadlines" Same / Varies — the served `per_company` — then
 * one deadline or one per company the QWP has, each with "Custom…"),
 * visibility of NCRs and RFIs (the served yes/no) and AI-assisted drafting.
 * Where the rule does not exist in a QWP (v83 `naMode`: visibility without
 * three parties, RFIs in internal QA — the grammar's `forcedNull`) there are no
 * fields: "Record as not applicable" saves the subarea (the same PUT), and once
 * the server holds it as `null` the editor says "Not applicable." with v83's
 * reason and offers nothing to save.
 *
 * ONE RULE, ONE SCOPE AT A TIME. "All QWPs · common project rule" edits the
 * first row of the grammar (`setGeneral`: every package that follows it, and
 * the automatic ones re-adapted); a QWP edits that package's own rule
 * (`addOwnRule` + `setPackageRule`). Nothing is saved here that today's editor
 * would not save: "Save in draft" is `useQmpRuleEditor.save(section)`, the
 * same `PUT /qmp/packages/rules/{section}` with EVERY live package (D11.1);
 * "Follow the common rule again" is `removeOwnRule`, then the same save;
 * "Restore standard rules" is `useQmpRuleEditor.restore(section)`
 * (`POST /qmp/draft/restore-standard`, by sub-step code). Cancel puts the
 * subarea back as the server holds it (`initSection`).
 *
 * THE CHOICES ARE THE SERVER'S: every select is `standard_options`; "Custom…"
 * appears where the field is served with `custom` (its units and minimum),
 * and a custom duration is sent as `{value, unit}` — `qmp_parse_rule` accepts
 * any `QmpDuration` (value >= 1). The server's refusal is shown as it comes.
 *
 * "Unsaved changes" (v83 l. 2303): while the person has changed something not
 * saved, switching QWP is refused here, and the page refuses the rail and the
 * Approvals link (`onDirty`). Cancel always leaves, discarding the changes.
 */
import React from 'react';
import {
  canonical, sameValue, fieldOptions, positionsOf, forcedNull, initSection,
  setGeneral, addOwnRule, removeOwnRule, setPackageRule, valueOf,
  formatNotice, formatSignoffDue, formatDocRelease, formatResponseTime,
  formatIfOverdue, samePreparedBy, POSITION_LABEL,
  canVaryByCompany, setPerCompany, dueKeys, DUE_POSITION, formatAi, packageValue,
} from '../../lib/qmpRules';
import { reviewText } from './ItpRulesStep';

/** The rules this editor draws (step 2; QMP-FE-8e2: step 3). */
export const FULL_EDITOR_SECTIONS = ['itp_rules', 'hold_points', 'witness_points'];
export const STEP3_EDITOR_SECTIONS = ['ncr', 'rfi', 'visibility', 'ai'];

export const UNSAVED_LEAVE = 'You have unsaved changes. Save in draft or Cancel before leaving this screen.';
export const UNSAVED_SCOPE = 'Save or cancel your changes before switching to another QWP.';
export const CUSTOM_INCOMPLETE = 'Custom duration: enter a whole number (1 or more) and choose the unit.';
export const SAVED = 'Saved in draft. Nothing is published yet.';
export const FOLLOWS_AGAIN = 'This QWP follows the common rule again.';
export const restoredText = (title) => `Standard rules restored for ${title} on every QWP — saved in draft.`;

// v83 tokens, dark values.
const H2 = 'text-[20px] leading-[1.3] mb-[7px] font-bold';
const INTRO = 'text-[#afbdd0] max-w-[72ch]';
const PANEL = 'bg-[#101a2b] border border-[#2b3c56] rounded-[10px] p-[18px] my-[16px] max-[580px]:p-[14px]';
const SMALL = 'text-[12px] text-[#afbdd0] font-normal';
const FIELD = 'flex flex-col gap-[6px] font-bold my-[12px]';
const CONTROL = 'w-full min-w-0 max-w-full bg-[#0b1423] text-[#f1f5fb] border border-[#2b3c56] rounded-[7px] '
  + 'p-[11px] min-h-[44px] font-normal disabled:opacity-60 max-[580px]:text-[16px]';
const GRID = 'grid grid-cols-2 gap-[16px] max-[580px]:grid-cols-1 max-[580px]:gap-0';
const INFO = 'border-l-[3px] border-[#ff8b32] px-[13px] py-[10px] bg-[#32251e] my-[14px] text-[#f1f5fb] [&_strong]:font-bold';
const REVIEW = 'border-l-[3px] border-[#ffa9b2] px-[14px] py-[12px] bg-[#3a1c24] my-[12px] text-[#f1f5fb] [&_strong]:font-bold';
const DANGER = 'text-[#ffa9b2]';
const SUBHEAD = 'mt-[16px] mb-0 text-[13px] text-[#afbdd0] tracking-[.04em] font-bold';
const PENDING = 'border-l-[3px] border-[#ffd28b] px-[14px] py-[12px] bg-[#332819] my-[12px] text-[#f1f5fb] [&_strong]:font-bold';
const CHECK = 'flex items-start gap-[10px] font-normal my-[6px] min-h-[32px] cursor-pointer';
const CHECKBOX = 'w-[20px] h-[20px] min-h-0 flex-[0_0_20px] p-0 mt-[1px] accent-[#ff7a12]';
const CUSTOM_ROW = 'flex items-center gap-[8px] mt-[6px] flex-wrap font-normal';
const NUMBER = 'max-w-[120px] bg-[#0b1423] text-[#f1f5fb] border border-[#2b3c56] rounded-[7px] p-[11px] min-h-[44px] '
  + 'disabled:opacity-60 max-[580px]:text-[16px]';
const UNIT = 'max-w-[170px] bg-[#0b1423] text-[#f1f5fb] border border-[#2b3c56] rounded-[7px] px-[10px] py-[8px] '
  + 'min-h-[40px] disabled:opacity-60 max-[580px]:text-[16px]';
const BUTTON = 'min-h-[44px] border border-[#2b3c56] rounded-[7px] px-[13px] py-[9px] text-[#f1f5fb] bg-[#0b1423] '
  + 'whitespace-normal disabled:opacity-45 disabled:cursor-not-allowed';
const PRIMARY = 'min-h-[44px] border border-[#ff7a12] rounded-[7px] px-[13px] py-[9px] bg-[#ff7a12] text-[#17202d] '
  + 'font-bold whitespace-normal disabled:opacity-45 disabled:cursor-not-allowed';
const QUIET = 'min-h-[44px] border border-transparent rounded-[7px] px-[4px] py-[9px] bg-transparent text-[#ff8b32] '
  + 'text-left disabled:opacity-45';
const END = 'flex items-center justify-between gap-[12px] flex-wrap pt-[12px] mt-[18px] border-t border-[#2b3c56]';
const ERROR = 'bg-[#332819] text-[#ffa9b2] px-[12px] py-[10px] rounded-[6px] mb-[14px] text-[13px] flex items-center gap-[10px] flex-wrap';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const CUSTOM_KEY = '__custom__';
const UNIT_LABEL = { hours: 'hours', working_days: 'working days' };
const isDuration = (v) => Boolean(v && typeof v === 'object' && typeof v.value === 'number' && v.unit);
const wholeAtLeast = (n, min) => /^\d+$/.test(String(n)) && Number(n) >= (min || 1);

/** The served "Custom…" of a field: `{units, min}`, or null. */
const customOf = (options, section, field) => (
  (options && options[section] && options[section][field] && options[section][field].custom) || null);

/** v83 `durField`: the served presets, "Custom…" (a whole number >= the
 *  served minimum + a served unit, `suffix` = before/after). A custom value
 *  being typed is kept here (`draft`) until it is whole; only then is it
 *  written to the rule. */
function DurationField({
  label, hint, suffix, served, custom, current, fallback, onPick, draft, onDraft, disabled, testId,
}) {
  const presets = (served || []).map((o) => ({ key: canonical(o.value), label: o.label, value: o.value }));
  const hit = presets.find((o) => sameValue(o.value, current));
  const opts = presets.slice();
  let selected;
  if (draft) selected = CUSTOM_KEY;
  else if (hit) selected = hit.key;
  else if (custom && isDuration(current)) selected = CUSTOM_KEY;
  else {
    const extra = { key: canonical(current), label: fallback ? fallback(current) : '', value: current };
    opts.push(extra);
    selected = extra.key;
  }
  if (custom) opts.push({ key: CUSTOM_KEY, label: 'Custom…' });
  const units = (custom && custom.units) || [];
  const shown = draft || (isDuration(current) ? { n: String(current.value), unit: current.unit } : { n: '', unit: units[0] });
  const write = (next) => {
    onDraft(next);
    if (wholeAtLeast(next.n, custom && custom.min)) onPick({ value: Number(next.n), unit: next.unit });
  };
  return (
    <label className={FIELD}>
      {label}
      <select
        className={CONTROL}
        value={selected}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === CUSTOM_KEY) {
            // v83 `durField`: Custom… from a preset starts empty.
            onDraft({ n: '', unit: isDuration(current) && units.includes(current.unit) ? current.unit : units[0] });
            return;
          }
          const o = opts.find((x) => x.key === e.target.value);
          onDraft(null);
          if (o) onPick(o.value === undefined ? null : JSON.parse(JSON.stringify(o.value)));
        }}
        data-testid={testId}
      >
        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {selected === CUSTOM_KEY && (
        <span className={CUSTOM_ROW}>
          <input
            type="number"
            min={(custom && custom.min) || 1}
            step="1"
            className={NUMBER}
            value={shown.n}
            placeholder="1 or more"
            disabled={disabled}
            aria-label={`${label} — number`}
            onChange={(e) => write({ ...shown, n: e.target.value })}
            data-testid={`${testId}-n`}
          />
          <select
            className={UNIT}
            value={shown.unit}
            disabled={disabled}
            aria-label={`${label} — unit`}
            onChange={(e) => write({ ...shown, unit: e.target.value })}
            data-testid={`${testId}-u`}
          >
            {units.map((u) => <option key={u} value={u}>{UNIT_LABEL[u] || u}</option>)}
          </select>
          {suffix && <span className={SMALL}>{suffix}</span>}
        </span>
      )}
      {hint && <span className={SMALL}>{hint}</span>}
    </label>
  );
}

/** v83 `field`: a select of served choices. */
function SelectField({
  label, hint, served, current, fallback, same, onPick, disabled, testId,
}) {
  const opts = (served || []).map((o) => ({ key: canonical(o.value), label: o.label, value: o.value }));
  const cmp = same || sameValue;
  let hit = opts.find((o) => cmp(o.value, current));
  if (!hit) {
    hit = { key: canonical(current), label: fallback ? fallback(current) : '', value: current };
    opts.push(hit);
  }
  return (
    <label className={FIELD}>
      {label}
      <select
        className={CONTROL}
        value={hit.key}
        disabled={disabled}
        onChange={(e) => {
          const o = opts.find((x) => x.key === e.target.value);
          if (o) onPick(JSON.parse(JSON.stringify(o.value)));
        }}
        data-testid={testId}
      >
        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {hint && <span className={SMALL}>{hint}</span>}
    </label>
  );
}

// v83 `deadlineFields` — preparation (l. 1964–1975).
function PreparationFields({
  value, row, onChange, disabled, options, names, draftOf, setDraft,
}) {
  const v = value || {};
  const e = v.endorsement || {};
  const have = row ? positionsOf(row) : null;
  const prepared = fieldOptions(options, 'itp_rules', 'prepared_by')
    .filter((o) => !have || (o.value || []).every((ref) => have.includes(ref.position)));
  const labelOf = (ref) => (ref && ref.position ? POSITION_LABEL[ref.position] : (ref && names[ref.company_id]) || '—');
  const servedLabel = {};
  fieldOptions(options, 'itp_rules', 'endorsement.endorsed_by[]').forEach((o) => {
    if (o.value && o.value.position) servedLabel[o.value.position] = o.label;
  });
  const list = e.endorsed_by || [];
  const setList = (next) => onChange({
    ...v, endorsement: { ...e, required: next.length > 0, endorsed_by: next },
  });
  const setE = (patch) => onChange({ ...v, endorsement: { ...e, ...patch } });
  const positions = ['execution', 'verification', 'acceptance'].filter((p) => servedLabel[p]);
  const companies = list.filter((ref) => ref && ref.company_id);
  return (
    <>
      <SelectField
        label="ITP prepared by"
        hint="The party responsible for creating the ITPs."
        served={prepared}
        current={v.prepared_by || []}
        fallback={(refs) => (refs || []).map(labelOf).join(' or ') || '—'}
        same={samePreparedBy}
        disabled={disabled}
        onPick={(x) => onChange({ ...v, prepared_by: x })}
        testId="re-prepared"
      />
      <fieldset className="border-0 p-0 my-[12px]" data-testid="re-endorse">
        <legend className="font-bold p-0 mb-[6px]">Endorsed by</legend>
        {positions.map((pos) => {
          const on = list.some((ref) => ref && ref.position === pos);
          const lacks = Boolean(row && !have.includes(pos));
          let note = null;
          if (lacks) {
            note = on
              ? <span className={DANGER}>{`${row.name} has no ${servedLabel[pos]} — untick it.`}</span>
              : `${row.name} has no ${servedLabel[pos]}.`;
          }
          return (
            <label key={pos} className={CHECK}>
              <input
                type="checkbox"
                className={CHECKBOX}
                checked={on}
                disabled={disabled || (lacks && !on)}
                onChange={() => setList(on
                  ? list.filter((ref) => !(ref && ref.position === pos))
                  : list.concat([{ position: pos }]))}
                data-testid={`re-endorse-${pos}`}
              />
              <span>
                {servedLabel[pos]}
                {note && <span className={`${SMALL} block`} data-testid={`re-endorse-note-${pos}`}>{note}</span>}
              </span>
            </label>
          );
        })}
        {companies.map((ref) => (
          <label key={ref.company_id} className={CHECK}>
            <input
              type="checkbox"
              className={CHECKBOX}
              checked
              disabled={disabled}
              onChange={() => setList(list.filter((x) => !(x && x.company_id === ref.company_id)))}
              data-testid={`re-endorse-co-${ref.company_id}`}
            />
            <span>{labelOf(ref)}</span>
          </label>
        ))}
        <span className={SMALL}>
          If more than one is ticked, all of them must endorse. Leave all unticked for
          {' '}
          <b>Not required</b>
          .
        </span>
      </fieldset>
      {e.required && (
        <div className={GRID}>
          <DurationField
            label="Response time"
            hint="A deadline to respond, not to agree. Time never endorses an ITP automatically."
            served={fieldOptions(options, 'itp_rules', 'endorsement.response_time')}
            custom={customOf(options, 'itp_rules', 'endorsement.response_time')}
            current={e.response_time ?? null}
            fallback={formatResponseTime}
            onPick={(x) => setE({ response_time: x })}
            draft={draftOf('response_time')}
            onDraft={(d) => setDraft('response_time', d)}
            disabled={disabled}
            testId="re-resp"
          />
          {e.response_time == null && !draftOf('response_time') ? (
            <div className={FIELD}>
              If overdue
              <span className={`${CONTROL} opacity-60`} data-testid="re-over">Never overdue</span>
            </div>
          ) : (
            <SelectField
              label="If overdue"
              hint="Proceed at risk is an explicit, signed act of the Executing Party; every ITC created that way is marked as issued at risk."
              served={fieldOptions(options, 'itp_rules', 'endorsement.if_overdue')}
              current={e.if_overdue}
              fallback={formatIfOverdue}
              disabled={disabled}
              onPick={(x) => setE({ if_overdue: x })}
              testId="re-over"
            />
          )}
        </div>
      )}
    </>
  );
}

// v83 `deadlineFields` — hold (l. 1976–1978).
function HoldFields({
  value, onChange, disabled, options, draftOf, setDraft,
}) {
  const v = value || {};
  const docServed = fieldOptions(options, 'hold_points', 'document_release_due');
  const sourceOf = (due) => {
    const o = docServed.find((x) => sameValue(x.value, due));
    return o && o.source ? o.source : 'qmp';
  };
  const dur = (field, label, hint, suffix, testId, fallback) => (
    <DurationField
      label={label}
      hint={hint}
      suffix={suffix}
      served={fieldOptions(options, 'hold_points', field)}
      custom={customOf(options, 'hold_points', field)}
      current={v[field] ?? null}
      fallback={fallback}
      onPick={(x) => onChange(field === 'document_release_due'
        ? { ...v, document_release_due: x, document_release_due_source: sourceOf(x) }
        : { ...v, [field]: x })}
      draft={draftOf(field)}
      onDraft={(d) => setDraft(field, d)}
      disabled={disabled}
      testId={testId}
    />
  );
  return (
    <>
      {dur('notice', 'Booking notice', 'Minimum notice before the inspection, to book it and notify the required parties.', 'before', 're-hnotice', formatNotice)}
      <div className={GRID}>
        {dur('signoff_due', 'Sign-off due (inspection)', 'After the booked inspection time, to release or reject the hold point.', 'after', 're-hdue', formatSignoffDue)}
        {dur('document_release_due', 'Release due (document)', 'After the evidence is submitted. No inspection is booked for a document hold.', '', 're-hdoc',
          (d) => formatDocRelease({ document_release_due: d }))}
      </div>
      <div className={INFO}>
        <strong>No response: overdue — and still held.</strong>
        {' '}
        Time never releases a hold point. Each ITP then sets which line items are hold points and who signs them.
      </div>
    </>
  );
}

// v83 `deadlineFields` — witness (l. 1979).
function WitnessFields({
  value, onChange, disabled, options, draftOf, setDraft,
}) {
  const v = value || {};
  const dur = (field, label, hint, suffix, testId, fallback) => (
    <DurationField
      label={label}
      hint={hint}
      suffix={suffix}
      served={fieldOptions(options, 'witness_points', field)}
      custom={customOf(options, 'witness_points', field)}
      current={v[field] ?? null}
      fallback={fallback}
      onPick={(x) => onChange({ ...v, [field]: x })}
      draft={draftOf(field)}
      onDraft={(d) => setDraft(field, d)}
      disabled={disabled}
      testId={testId}
    />
  );
  return (
    <>
      {dur('notice', 'Notice', 'Before the inspection window opens. With no minimum notice, notice can be given any time before the window opens.', 'before', 're-wnotice', formatNotice)}
      {dur('signoff_due', 'Sign-off due', 'After the inspection window closes.', 'after', 're-wdue', formatSignoffDue)}
      <div className={INFO}>
        If no sign-off is recorded by then, it is recorded as late. The ITC can continue without the witness
        sign-off; a rejection still has to be dealt with.
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// QMP-FE-8e2 — the rules of step 3 (v83 `deadlineFields` l. 1980–2002).
// ---------------------------------------------------------------------------

/** v83 `field('Deadlines', ..., SAMEVARY)` — the served `per_company`, only
 *  where the package has two companies (`per_company_needs_two_companies`). */
function PerCompanyField({
  section, value, row, onChange, disabled, options, onSplit,
}) {
  if (!canVaryByCompany(row)) return null;
  return (
    <SelectField
      label="Deadlines"
      hint="Choose “Varies between companies” where the companies follow different contracts."
      served={fieldOptions(options, section, 'per_company')}
      current={Boolean(value && value.per_company)}
      fallback={(x) => (x ? 'Varies between companies' : 'Same for all companies')}
      disabled={disabled}
      onPick={(x) => { onSplit(); onChange(setPerCompany(section, value, row, Boolean(x))); }}
      testId={`re-${section}-split`}
    />
  );
}

/** One deadline of a due field (`all` or one company), "Custom…" where served. */
function dueField(section, field, key, {
  value, onChange, disabled, options, draftOf, setDraft,
}, label, hint, testId) {
  const v = value || {};
  const dues = v[field] || {};
  const draftKey = `${field}.${key}`;
  return (
    <DurationField
      key={draftKey}
      label={label}
      hint={hint}
      served={fieldOptions(options, section, field)}
      custom={customOf(options, section, field)}
      current={dues[key] ?? null}
      fallback={formatResponseTime}
      onPick={(x) => onChange({ ...v, [field]: { ...dues, [key]: x } })}
      draft={draftOf(draftKey)}
      onDraft={(d) => setDraft(draftKey, d)}
      disabled={disabled}
      testId={testId}
    />
  );
}

/** The per-company deadlines of one field: the companies the row has. */
const dueGrid = (section, field, props, prefix) => (
  <div className={GRID}>
    {dueKeys(section, field, props.row).map((k) => dueField(
      section, field, k, props, POSITION_LABEL[DUE_POSITION[k]], null, `${prefix}-${k}`,
    ))}
  </div>
);

// v83 `deadlineFields` — ncr (l. 1980–1986).
function NcrFields(props) {
  const { value, setDraft } = props;
  const split = Boolean(value && value.per_company);
  const onSplit = () => {
    ['response_due', 'decision_due'].forEach((f) => ['all', 'executing', 'verification', 'acceptance']
      .forEach((k) => setDraft(`${f}.${k}`, null)));
  };
  return (
    <>
      <PerCompanyField section="ncr" {...props} onSplit={onSplit} />
      {split ? (
        <>
          <h3 className={SUBHEAD}>Response due</h3>
          {dueGrid('ncr', 'response_due', props, 're-ncr-resp')}
          <h3 className={SUBHEAD}>Decision due</h3>
          {dueGrid('ncr', 'decision_due', props, 're-ncr-dec')}
        </>
      ) : (
        <div className={GRID}>
          {dueField('ncr', 'response_due', 'all', props, 'Response due',
            'The company that must fix it submits the root cause and corrective action.', 're-ncr-resp')}
          {dueField('ncr', 'decision_due', 'all', props, 'Decision due',
            'Each company chosen to accept the NCR approves or rejects what is submitted.', 're-ncr-dec')}
        </div>
      )}
      <div className={INFO}>
        An NCR never closes because a deadline has passed. A late response or decision is recorded against that company.
      </div>
    </>
  );
}

// v83 `deadlineFields` — rfi (l. 1987–1993).
function RfiFields(props) {
  const { value, setDraft } = props;
  const split = Boolean(value && value.per_company);
  const onSplit = () => {
    ['all', 'executing', 'verification', 'acceptance'].forEach((k) => setDraft(`answer_due.${k}`, null));
  };
  return (
    <>
      <PerCompanyField section="rfi" {...props} onSplit={onSplit} />
      {split
        ? dueGrid('rfi', 'answer_due', props, 're-rfi-due')
        : dueField('rfi', 'answer_due', 'all', props, 'Answer due',
          'How long the company that received the RFI has to answer.', 're-rfi-due')}
      <div className={INFO}>
        ConstructQA sends reminders and records a late answer. Time never closes an RFI.
      </div>
    </>
  );
}

// v83 `deadlineFields` — access (l. 1994–1997): the served yes/no choices.
function VisibilityFields({
  value, onChange, disabled, options,
}) {
  const v = value || {};
  const yn = (field, label) => (
    <SelectField
      label={label}
      served={fieldOptions(options, 'visibility', field)}
      current={Boolean(v[field])}
      fallback={(x) => (x ? 'Sees them' : 'Does not see them')}
      disabled={disabled}
      onPick={(x) => onChange({ ...v, [field]: Boolean(x) })}
      testId={`re-vis-${field}`}
    />
  );
  return (
    <>
      <h3 className={SUBHEAD}>Acceptance Party</h3>
      <div className={GRID}>
        {yn('acceptance_sees_ev_ncrs', 'See Executing–Verification NCRs?')}
        {yn('acceptance_sees_ev_rfis', 'See Executing–Verification RFIs?')}
      </div>
      <h3 className={SUBHEAD}>Executing Party</h3>
      <div className={GRID}>
        {yn('executing_sees_va_ncrs', 'See Verification–Acceptance NCRs?')}
        {yn('executing_sees_va_rfis', 'See Verification–Acceptance RFIs?')}
      </div>
      <div className={INFO}>
        Companies always see the records they are directly involved in. These questions only decide whether the
        third company in the quality chain can also see them.
      </div>
    </>
  );
}

// v83 `deadlineFields` — ai (l. 1998): the served `allowed`, the whole rule.
function AiFields({
  value, onChange, disabled, options,
}) {
  const served = fieldOptions(options, 'ai', 'allowed').map((o) => ({ label: o.label, value: { allowed: o.value } }));
  return (
    <>
      <SelectField
        label="AI-assisted drafting"
        served={served}
        current={value}
        fallback={formatAi}
        disabled={disabled}
        onPick={(x) => onChange(x)}
        testId="re-ai"
      />
      <div className={INFO}>
        AI can help prepare draft content, such as an ITP draft. It cannot sign, endorse, approve or make quality
        decisions. Those responsibilities always remain with a person.
      </div>
    </>
  );
}

const FIELDS = {
  itp_rules: PreparationFields,
  hold_points: HoldFields,
  witness_points: WitnessFields,
  ncr: NcrFields,
  rfi: RfiFields,
  visibility: VisibilityFields,
  ai: AiFields,
};

/** v83 `naMode`: the rule does not exist in this QWP (visibility without three
 *  parties, RFIs in internal QA) — the grammar's `forcedNull`. */
export const naMode = (section, row) => Boolean(row && forcedNull(section, row));

/** v83 `naBlock` text (l. 1988, 1995). */
export function naText(section, row) {
  if (section === 'rfi') return `${row.name} is internal QA: there are no RFIs between companies.`;
  const one = !row.verification && !row.acceptance;
  return `${row.name} now has ${one ? 'one party' : 'two parties'}. These questions only apply to packages with `
    + 'an Executing, a Verification and an Acceptance Party.';
}

/** v83 `isNa`: the server already holds this rule as not applicable for the
 *  package (saved in the draft, as `null`). */
export function recordedNa(section, ctx, row) {
  const entry = ctx && ctx.packages && ctx.packages[row.qwp_id];
  const draft = entry && entry.draft;
  if (!draft || !(draft.sections_saved || []).includes(section)) return false;
  return packageValue(section, entry, ctx.standardRules, row) == null;
}

/** v83 `scopePicker` suffix of a QWP, from the grammar's row kind. */
export function scopeSuffix(section, line, row) {
  const kind = (line && line.kind) || 'follow';
  if (kind === 'own') return ' · rule of its own';
  if (kind === 'auto') return forcedNull(section, row) ? ' · not applicable' : ' · standard rule, adapted to its parties';
  return ' · follows the common rule';
}

/** v83 "Who this change reaches". The automatic rows are re-adapted from the
 *  common rule, so they are reached too; only own rules stay as they are. */
export function reachText(section, state, structure, row, recorded) {
  const rows = (state && state.rows) || {};
  if (!row) {
    const all = structure || [];
    const reached = all.filter((r) => {
      const k = (rows[r.qwp_id] || {}).kind || 'follow';
      return k === 'follow' || (k === 'auto' && !forcedNull(section, r));
    });
    return `${plural(reached.length, 'QWP', 'QWPs')} now, and every new QWP without a rule of its own.`
      + (reached.length !== all.length ? ' QWPs with a rule of their own stay as they are.' : '');
  }
  // QMP-FE-8e2 — v83 `scopeInfo` for a rule that does not exist in the QWP.
  if (naMode(section, row)) {
    return recorded
      ? `Recorded as not applicable for ${row.name}.`
      : `Saving records this rule as not applicable for ${row.name}. If the package gets its parties back, the rule is asked for again.`;
  }
  const kind = (rows[row.qwp_id] || {}).kind || 'follow';
  if (kind === 'own') return `${row.name} already has a rule of its own.`;
  if (kind === 'auto') return `${row.name} has the standard rule, adapted to its parties. Saving gives it a rule of its own.`;
  return `${row.name} follows the common rule. Saving gives it a rule of its own.`;
}

export default function RuleEditorPage({
  section, editor, readOnly, inForce, revision, onClose, onFeedback, onDirty,
}) {
  const [scope, setScope] = React.useState('project');
  const [touched, setTouched] = React.useState(false);
  const [drafts, setDrafts] = React.useState({});
  const [asking, setAsking] = React.useState(false);
  const [pending, setPending] = React.useState(null);
  const state = editor.states[section];
  const { ctx } = editor;
  const structure = ctx.structure || [];
  const row = scope === 'project' ? null : structure.find((r) => r.qwp_id === scope) || null;
  const status = editor.status[section] || {};
  const incomplete = Object.values(drafts).some((d) => d && !wholeAtLeast(d.n, 1));
  const dirty = Boolean(touched && (status.dirty || incomplete));
  const busy = Boolean(editor.saving);
  const off = readOnly || busy;
  const title = editor.titles[section] || '';

  React.useEffect(() => { if (onDirty) onDirty(dirty); }, [dirty, onDirty]);
  React.useEffect(() => () => { if (onDirty) onDirty(false); }, [onDirty]);

  // "Follow the common rule again" saves the subarea AFTER the row went back
  // to the common rule, so the save reads the state with it.
  const { save } = editor;
  React.useEffect(() => {
    if (!pending) return;
    const msg = pending;
    setPending(null);
    (async () => {
      const ok = await save(section);
      if (ok) onClose(msg);
    })();
  }, [pending, save, section, onClose]);

  if (!state) return null;
  const line = row ? state.rows[row.qwp_id] : null;
  const value = row ? valueOf(state, row.qwp_id) : state.general;
  const names = {};
  (editor.companies || []).forEach((c) => { names[c.id] = c.name || c.short_name || ''; });
  const review = row && !inForce ? (status.review || {})[row.qwp_id] : null;
  const err = editor.errors[section];
  const errRow = err && err.qwpId ? structure.find((r) => r.qwp_id === err.qwpId) : null;

  const change = (next) => {
    if (readOnly) return;
    setTouched(true);
    if (!row) {
      editor.update(section, (st) => setGeneral(section, st, next, ctx));
      return;
    }
    const id = row.qwp_id;
    editor.update(section, (st) => {
      const base = (st.rows[id] || {}).kind === 'own' ? st : addOwnRule(st, id);
      return setPackageRule(base, id, next);
    });
  };
  const draftOf = (k) => drafts[k] || null;
  const setDraft = (k, d) => {
    setTouched(true);
    setDrafts((prev) => {
      const out = { ...prev };
      if (d) out[k] = d; else delete out[k];
      return out;
    });
  };

  const onScope = (next) => {
    if (dirty) { onFeedback(UNSAVED_SCOPE, true); return; }
    onFeedback('');
    setScope(next);
    setTouched(false);
    setDrafts({});
    setAsking(false);
  };
  const onCancel = () => {
    if (touched) editor.update(section, () => initSection(section, ctx));
    onClose('');
  };
  const onSave = async () => {
    if (incomplete) { onFeedback(CUSTOM_INCOMPLETE, true); return; }
    onFeedback('');
    const ok = await editor.save(section);
    if (ok) onClose(SAVED);
  };
  const onFollow = () => {
    if (dirty) { onFeedback(UNSAVED_LEAVE, true); return; }
    onFeedback('');
    editor.update(section, (st) => removeOwnRule(section, st, row.qwp_id, ctx));
    setPending(FOLLOWS_AGAIN);
  };
  const onRestore = async () => {
    setAsking(false);
    onFeedback('');
    const ok = await editor.restore(section);
    if (ok) onClose(restoredText(title));
  };

  const Fields = FIELDS[section];
  let intro = 'Edit the common rule, or give one QWP a rule of its own.';
  if (readOnly) intro = revision ? `Revision ${revision} — view only.` : 'View only.';
  const na = naMode(section, row);
  const recorded = na && recordedNa(section, ctx, row);
  const canFollow = Boolean(row && line && line.kind === 'own' && !readOnly && !na);
  let primary = 'Save in draft';
  if (na) primary = recorded ? '' : 'Record as not applicable';

  return (
    <div data-testid="re-page" data-section={section}>
      <h2 className={H2} data-testid="re-title">{title}</h2>
      <p className={INTRO} data-testid="re-intro">{intro}</p>
      {review && (
        <div className={REVIEW} data-testid="re-review">
          <strong>Needs review.</strong>
          {` ${reviewText(review)} Saved rules are never rewritten when parties change — choose what fits ${row.name} now.`}
        </div>
      )}
      {err && (
        <div className={ERROR} role="alert" data-testid="re-error">
          <span>{errRow ? `${errRow.name}: ${err.message}` : err.message}</span>
          {err.retry && (
            <button type="button" className={BUTTON} disabled={busy} onClick={onSave} data-testid="re-retry">
              Try again
            </button>
          )}
        </div>
      )}
      <section className={PANEL} data-testid="re-panel">
        <label className={FIELD}>
          Quality Work Package
          <select
            className={CONTROL}
            value={scope}
            disabled={readOnly}
            onChange={(e) => onScope(e.target.value)}
            data-testid="re-scope"
          >
            <option value="project">All QWPs · common project rule</option>
            {structure.map((r) => (
              <option key={r.qwp_id} value={r.qwp_id}>
                {`${r.name}${scopeSuffix(section, state.rows[r.qwp_id], r)}`}
              </option>
            ))}
          </select>
        </label>
        <div className={INFO} data-testid="re-reach">
          <strong>Who this change reaches</strong>
          <br />
          {reachText(section, state, structure, row, recorded)}
        </div>
        {na && recorded && (
          <div className={PENDING} data-testid="re-na">
            <strong>Not applicable.</strong>
            {` ${naText(section, row)}`}
          </div>
        )}
        {Fields && !na && (
          <Fields
            key={scope}
            value={value}
            row={row}
            onChange={change}
            disabled={off}
            options={editor.options}
            names={names}
            draftOf={draftOf}
            setDraft={setDraft}
          />
        )}
        {!readOnly && !asking && (
          <div className="mt-[14px] pt-[12px] border-t border-[#2b3c56]">
            <button type="button" className={QUIET} disabled={busy} onClick={() => setAsking(true)} data-testid="re-restore">
              Restore standard rules
            </button>
          </div>
        )}
        {!readOnly && asking && (
          <div className={`${INFO} mt-[14px] px-[14px] py-[12px]`} data-testid="re-restore-ask">
            <strong>{`Restore ConstructQA’s standard rules for ${title}, on every QWP?`}</strong>
            <br />
            <span className={SMALL}>
              Rules of their own in this section are replaced. Where the standard names a party a QWP does not
              have, it is adapted to that QWP — and a saved rule that needed review no longer does.
            </span>
            <div className="flex gap-[10px] flex-wrap mt-[10px]">
              <button type="button" className={PRIMARY} disabled={busy} onClick={onRestore} data-testid="re-restore-yes">
                Restore
              </button>
              <button type="button" className={BUTTON} onClick={() => setAsking(false)} data-testid="re-restore-no">
                Keep my rules
              </button>
            </div>
          </div>
        )}
      </section>
      <div className={END} data-testid="re-footer">
        <button type="button" className={BUTTON} onClick={onCancel} data-testid="re-cancel">
          {readOnly ? 'Back' : 'Cancel'}
        </button>
        {canFollow && (
          <button type="button" className={BUTTON} disabled={busy} onClick={onFollow} data-testid="re-follow">
            Follow the common rule again
          </button>
        )}
        <span className="flex-1 text-[12px] text-[#afbdd0] max-[580px]:basis-full" />
        {!readOnly && primary && (
          <button type="button" className={PRIMARY} disabled={busy} onClick={onSave} data-testid="re-save">
            {primary}
          </button>
        )}
      </div>
    </div>
  );
}
